import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { GymClassificationEditor, gymValidationMessage } from '../../../components/gym/GymClassificationEditor';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../../../components/ui/dialog';
import {
  ChevronLeft, Plus, Trash2, Save, AlertTriangle,
  Users, User, Loader2, Sparkles, Calendar, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { ExerciseSelector } from '../../../components/ExerciseSelector';
import { prescriptionSuggestionKey, getPrescriptionSuggestions } from '../../../lib/prescriptionSuggestions';
import { BodyWeightSuggestion } from '../../../components/reports/BodyWeightSuggestion';

const CATEGORIES = ['STRENGTH', 'AEROBIC', 'RTP', 'RECOVERY', 'PLYOMETRIC', 'MIXED'];
const BLOCK_TYPES = ['STRENGTH', 'AEROBIC', 'RTP', 'RECOVERY', 'PLYOMETRIC'];
const OBJECTIVES = ['MAX_STRENGTH', 'STRENGTH_SPEED', 'POWER', 'STRENGTH_ENDURANCE'];
const OBJECTIVE_LABELS = {
  MAX_STRENGTH: 'Max Strength', STRENGTH_SPEED: 'Strength-Speed',
  POWER: 'Power', STRENGTH_ENDURANCE: 'Strength Endurance',
};
const JUMP_FOCUSES = [
  'REACTIVE_STRENGTH', 'ELASTIC_STIFFNESS', 'EXPLOSIVE_POWER',
  'ASYMMETRY_CORRECTION', 'NEURAL_ACTIVATION',
  'LANDING_DECELERATION_CONTROL', 'RTP_RETURN_TO_JUMP',
];
const JUMP_FOCUS_LABELS = {
  REACTIVE_STRENGTH: 'Reactive Strength',
  ELASTIC_STIFFNESS: 'Elastic / Stiffness',
  EXPLOSIVE_POWER: 'Explosive Power',
  ASYMMETRY_CORRECTION: 'Asymmetry Correction',
  NEURAL_ACTIVATION: 'Neural Activation',
  LANDING_DECELERATION_CONTROL: 'Landing / Deceleration',
  RTP_RETURN_TO_JUMP: 'RTP Return-to-Jump',
};
const INTENSITY_LEVELS = ['LOW', 'MODERATE', 'HIGH'];
const DAY_LABELS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

const CAT_COLORS = {
  STRENGTH: 'border-blue-500/30 bg-blue-500/10 text-blue-400',
  AEROBIC: 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400',
  RTP: 'border-amber-500/30 bg-amber-500/10 text-amber-400',
  RECOVERY: 'border-purple-500/30 bg-purple-500/10 text-purple-400',
  PLYOMETRIC: 'border-orange-500/30 bg-orange-500/10 text-orange-400',
};

function emptyExercise(ex = null) {
  return {
    exercise_id: ex?.exercise_id || ex?.id || '', exercise_name: ex?.exercise_name || ex?.name || '',
    sets: ex?.sets || ex?.default_sets || ex?.default_params?.sets || '',
    reps: ex?.reps || ex?.default_reps || ex?.default_params?.reps || '',
    load_mode: ex?.load_mode || 'ABSOLUTE', load_kg: '', load_pct: '', load_pct_bw: '',
    velocity_target: '',
    duration_sec: ex?.duration_sec || ex?.default_duration || ex?.default_params?.duration_sec || '',
    work_sec: ex?.default_params?.work_sec || '',
    rest_sec: ex?.default_params?.rest_sec || '',
    intensity_pct: ex?.default_params?.intensity_pct || '',
    distance_m: '', target_speed_kmh: '', notes: '',
    contacts: ex?.default_params?.contacts || '', intensity_level: '',
    is_vbt_linked: ex?.is_vbt_linked || false, vbt_lift_type: ex?.vbt_lift_type || null,
    load_supported: ex?.load_supported !== false,
  };
}

function emptyBlock() {
  return { name: '', block_type: 'STRENGTH', training_objective: '', jump_focus: '', exercises: [], notes: '' };
}

function emptyDay(dow) {
  return {
    day_of_week: dow, day_label: DAY_LABELS[dow],
    blocks: [], session_id: null, notes: '',
    // Phase 2 canonical-integration
    day_category: '', duration_planned_min: '', start_time: '', title: '',
  };
}

const DAY_CATEGORIES = ['AEROBIC', 'STRENGTH', 'PLYOMETRIC', 'RECOVERY', 'RTP'];
const DAY_CATEGORY_LABELS = {
  AEROBIC: { it: 'Aerobico', en: 'Aerobic' },
  STRENGTH: { it: 'Forza', en: 'Strength' },
  PLYOMETRIC: { it: 'Pliometrico', en: 'Plyometric' },
  RECOVERY: { it: 'Recupero', en: 'Recovery' },
  RTP: { it: 'RTP', en: 'RTP' },
};

/* ======================================================
   NumericInput — locally-controlled input that only
   propagates to parent on blur / Enter.
   This prevents the parent re-render from stealing focus.
   ====================================================== */
function NumericInput({ value, onChange, ...props }) {
  const [local, setLocal] = useState(value ?? '');
  const ref = useRef(null);
  const parentValue = useRef(value);

  // Sync from parent only when it changes externally (not from our own blur)
  useEffect(() => {
    if (value !== parentValue.current) {
      parentValue.current = value;
      // Only overwrite local if the input is NOT focused (user isn't typing)
      if (ref.current !== document.activeElement) {
        setLocal(value ?? '');
      }
    }
  }, [value]);

  const handleChange = (e) => {
    setLocal(e.target.value);
  };

  const flush = () => {
    if (local !== (value ?? '')) {
      parentValue.current = local;
      onChange({ target: { value: local } });
    }
  };

  const handleBlur = () => flush();
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { flush(); e.target.blur(); }
  };

  return (
    <Input
      ref={ref}
      value={local}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      {...props}
    />
  );
}

/* ======================================================
   SystemSuggestionCard — exercise-filtered, non-destructive
   jump-test suggestion. Fetches context for the currently
   selected exercise + focus athlete. NEVER auto-applies.
   ====================================================== */
const SystemSuggestionCard = React.memo(function SystemSuggestionCard({ bi, ei, exerciseName, athleteId, onApply }) {
  const { api } = useAuth();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function fetchCtx() {
      setLoading(true);
      setCtx(null);
      try {
        const res = await api.get('/prescriptions/exercise-jump-context', {
          params: { athlete_id: athleteId, exercise_name: exerciseName },
        });
        if (!cancelled) setCtx(res.data);
      } catch {
        if (!cancelled) setCtx(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    setDismissed(false);
    if (exerciseName && athleteId) fetchCtx();
    return () => { cancelled = true; };
  }, [api, exerciseName, athleteId]);

  if (dismissed) return null;
  if (loading) {
    return (
      <div className="mt-3 border-t border-zinc-700/50 pt-2 text-[11px] text-zinc-500" data-testid={`sys-suggestion-loading-${bi}-${ei}`}>
        Loading suggestion…
      </div>
    );
  }
  if (!ctx || ctx.classification == null) return null;

  const hasActionable = !!ctx.suggestion;
  const handleApply = () => {
    if (!ctx.suggestion) return;
    const s = ctx.suggestion;
    // Copy midpoint (min) of each range into prescription fields
    onApply({
      sets: s.sets_min ?? '',
      reps: s.reps_min ?? '',
      contacts: s.contacts_min ?? '',
      intensity_level: s.intensity_level || '',
    });
    setDismissed(true);
  };

  return (
    <div
      className="mt-3 border border-dashed border-cyan-500/30 bg-cyan-500/5 rounded-sm p-2.5"
      data-testid={`sys-suggestion-${bi}-${ei}`}
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="flex items-center gap-1.5">
          <Sparkles className="w-3 h-3 text-cyan-400" />
          <span className="text-[10px] font-semibold text-cyan-400 uppercase tracking-wider">System Suggestion</span>
        </div>
        <button
          onClick={() => setDismissed(true)}
          className="text-[10px] text-zinc-500 hover:text-zinc-300"
          data-testid={`sys-suggestion-dismiss-${bi}-${ei}`}
        >
          Keep My Prescription
        </button>
      </div>

      {/* Metrics */}
      {ctx.metrics && Object.keys(ctx.metrics).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-1.5 text-[11px]" data-testid={`sys-metrics-${bi}-${ei}`}>
          {ctx.metrics.cmj_height_cm != null && <span className="text-zinc-400">CMJ: <span className="text-zinc-200">{ctx.metrics.cmj_height_cm} cm</span></span>}
          {ctx.metrics.sj_height_cm != null && <span className="text-zinc-400">SJ: <span className="text-zinc-200">{ctx.metrics.sj_height_cm} cm</span></span>}
          {ctx.metrics.dj_rsi != null && <span className="text-zinc-400">RSI: <span className="text-zinc-200">{Number(ctx.metrics.dj_rsi).toFixed(2)}</span></span>}
          {ctx.metrics.dj_height_cm != null && <span className="text-zinc-400">DJ Height: <span className="text-zinc-200">{ctx.metrics.dj_height_cm} cm</span></span>}
          {ctx.metrics.dj_contact_time_ms != null && <span className="text-zinc-400">CT: <span className="text-zinc-200">{ctx.metrics.dj_contact_time_ms} ms</span></span>}
        </div>
      )}

      {/* Flags */}
      {ctx.flags && ctx.flags.length > 0 && (
        <div className="flex gap-1 mb-1.5 flex-wrap">
          {ctx.flags.map(f => (
            <Badge key={f} className={`text-[9px] ${
              f === 'IN_RTP' ? 'bg-rose-500/20 text-rose-400 border-rose-500/30' :
              f === 'LOW_RSI' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
              'bg-zinc-700/50 text-zinc-400'
            }`}>{f.replace(/_/g, ' ')}</Badge>
          ))}
        </div>
      )}

      {/* Message or Suggestion body */}
      {hasActionable ? (
        <>
          <div className="text-[11px] text-zinc-300 bg-zinc-900/40 rounded px-2 py-1.5 mb-1.5" data-testid={`sys-suggestion-values-${bi}-${ei}`}>
            Sets <span className="text-cyan-300 font-medium">{ctx.suggestion.sets_min}-{ctx.suggestion.sets_max}</span>
            {' | '}Reps <span className="text-cyan-300 font-medium">{ctx.suggestion.reps_min}-{ctx.suggestion.reps_max}</span>
            {' | '}Contacts <span className="text-cyan-300 font-medium">{ctx.suggestion.contacts_min}-{ctx.suggestion.contacts_max}</span>
            {' | '}Intensity <span className="text-cyan-300 font-medium">{ctx.suggestion.intensity_level}</span>
          </div>
          <div className="text-[10px] text-zinc-500 italic mb-2" data-testid={`sys-rationale-${bi}-${ei}`}>
            {ctx.suggestion.rationale}
          </div>
          <Button
            size="sm"
            onClick={handleApply}
            className="h-7 text-[11px] bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40"
            data-testid={`sys-suggestion-apply-${bi}-${ei}`}
          >
            Apply Suggestion
          </Button>
        </>
      ) : (
        <div className="text-[11px] text-zinc-400 italic" data-testid={`sys-suggestion-message-${bi}-${ei}`}>
          {ctx.message || 'No relevant test data available'}
        </div>
      )}
    </div>
  );
});

/* ======================================================
   ExerciseRow — individual exercise in a block
   Extracted to avoid re-mount on every keystroke
   ====================================================== */
const ExerciseRow = React.memo(function ExerciseRow({
  ex, ei, bi, blockType, trainingObjective, exerciseLib, suggestions, focusAthleteId, onUpdate, onUpdatePatch, onRemove, athleteIds, weightReference, testIdPrefix,
}) {
  const exLib = exerciseLib.find(e => e.id === ex.exercise_id);
  // Derive load_supported: prefer exercise state, fall back to library, then block default.
  const loadSupported = ex.load_supported !== undefined
    ? ex.load_supported !== false
    : (exLib?.load_supported !== undefined
        ? exLib.load_supported !== false
        : blockType === 'STRENGTH');
  const exSuggestions = getPrescriptionSuggestions(suggestions, { block_type: blockType, training_objective: trainingObjective }, ex)
    .filter(s => (athleteIds || []).includes(s.athlete_id));

  return (
    <div className="bg-zinc-900/60 rounded-sm p-3 border border-zinc-800" data-testid={`exercise-row-${bi}-${ei}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-zinc-200">{ex.exercise_name || 'Select exercise'}</span>
          {exLib?.is_vbt_linked && <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[9px]">VBT</Badge>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => onRemove(bi, ei)} className="text-zinc-500 hover:text-rose-400 h-6 w-6 p-0">
          <X className="w-3 h-3" />
        </Button>
      </div>

      {/* Param inputs */}
      <div className="grid grid-cols-3 sm:grid-cols-7 gap-2">
        {blockType === 'STRENGTH' && (
          <>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Sets</label>
              <NumericInput type="number" value={ex.sets} onChange={e => onUpdate(bi, ei, 'sets', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-sets-${bi}-${ei}`} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Reps</label>
              <NumericInput type="number" value={ex.reps} onChange={e => onUpdate(bi, ei, 'reps', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-reps-${bi}-${ei}`} />
            </div>
            {loadSupported && (
            <>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Load Mode</label>
              <select
                value={ex.load_mode || 'ABSOLUTE'}
                onChange={e => {
                  const mode = e.target.value;
                  // Single atomic patch — calling onUpdate twice in a row
                  // overwrites the first change with stale state.
                  onUpdatePatch(bi, ei, mode === 'PCT_BW'
                    ? { load_mode: mode, load_kg: '' }
                    : { load_mode: mode, load_pct_bw: '' });
                }}
                className="bg-zinc-800 border border-zinc-700 text-zinc-100 h-7 text-xs rounded w-full px-1"
                data-testid={`input-loadmode-${bi}-${ei}`}
              >
                <option value="ABSOLUTE">kg</option>
                <option value="PCT_BW">% BW</option>
              </select>
            </div>
            {(ex.load_mode || 'ABSOLUTE') === 'ABSOLUTE' ? (
              <div>
                <label className="text-[10px] text-zinc-500 block mb-0.5">Load (kg)</label>
                <NumericInput type="number" step="0.5" value={ex.load_kg} onChange={e => onUpdate(bi, ei, 'load_kg', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-load-${bi}-${ei}`} />
              </div>
            ) : (
              <div>
                <label className="text-[10px] text-zinc-500 block mb-0.5">% BW</label>
                <NumericInput type="number" step="1" value={ex.load_pct_bw} onChange={e => onUpdate(bi, ei, 'load_pct_bw', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-load-pctbw-${bi}-${ei}`} />
              </div>
            )}
            </>
            )}
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Velocity (m/s)</label>
              <NumericInput type="number" step="0.01" value={ex.velocity_target} onChange={e => onUpdate(bi, ei, 'velocity_target', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-vel-${bi}-${ei}`} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Rest (sec)</label>
              <NumericInput type="number" value={ex.rest_sec} onChange={e => onUpdate(bi, ei, 'rest_sec', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-rest-${bi}-${ei}`} />
            </div>
          </>
        )}
        {blockType === 'AEROBIC' && (
          <>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Sets</label>
              <NumericInput type="number" value={ex.sets} onChange={e => onUpdate(bi, ei, 'sets', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Reps</label>
              <NumericInput type="number" value={ex.reps} onChange={e => onUpdate(bi, ei, 'reps', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Work (sec)</label>
              <NumericInput type="number" value={ex.work_sec} onChange={e => onUpdate(bi, ei, 'work_sec', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-work-${bi}-${ei}`} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Rest (sec)</label>
              <NumericInput type="number" value={ex.rest_sec} onChange={e => onUpdate(bi, ei, 'rest_sec', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Intensity %</label>
              <NumericInput type="number" value={ex.intensity_pct} onChange={e => onUpdate(bi, ei, 'intensity_pct', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-intensity-${bi}-${ei}`} />
            </div>
          </>
        )}
        {blockType === 'PLYOMETRIC' && (
          <>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Sets</label>
              <NumericInput type="number" value={ex.sets} onChange={e => onUpdate(bi, ei, 'sets', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-sets-${bi}-${ei}`} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Reps</label>
              <NumericInput type="number" value={ex.reps} onChange={e => onUpdate(bi, ei, 'reps', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-reps-${bi}-${ei}`} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Contacts</label>
              <NumericInput type="number" value={ex.contacts} onChange={e => onUpdate(bi, ei, 'contacts', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-contacts-${bi}-${ei}`} />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Intensity</label>
              <Select value={ex.intensity_level || ''} onValueChange={v => onUpdate(bi, ei, 'intensity_level', v)}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-intensity-${bi}-${ei}`}>
                  <SelectValue placeholder="Level" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {INTENSITY_LEVELS.map(l => <SelectItem key={l} value={l} className="text-zinc-100 text-xs">{l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {loadSupported && (
              <>
                <div>
                  <label className="text-[10px] text-zinc-500 block mb-0.5">Load Mode</label>
                  <select
                    value={ex.load_mode || 'ABSOLUTE'}
                    onChange={e => {
                      const mode = e.target.value;
                      onUpdatePatch(bi, ei, mode === 'PCT_BW'
                        ? { load_mode: mode, load_kg: '' }
                        : { load_mode: mode, load_pct_bw: '' });
                    }}
                    className="bg-zinc-800 border border-zinc-700 text-zinc-100 h-7 text-xs rounded w-full px-1"
                    data-testid={`input-loadmode-${bi}-${ei}`}
                  >
                    <option value="ABSOLUTE">kg</option>
                    <option value="PCT_BW">% BW</option>
                  </select>
                </div>
                {(ex.load_mode || 'ABSOLUTE') === 'ABSOLUTE' ? (
                  <div>
                    <label className="text-[10px] text-zinc-500 block mb-0.5">Load (kg)</label>
                    <NumericInput type="number" step="0.5" value={ex.load_kg} onChange={e => onUpdate(bi, ei, 'load_kg', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-load-${bi}-${ei}`} />
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] text-zinc-500 block mb-0.5">% BW</label>
                    <NumericInput type="number" step="1" value={ex.load_pct_bw} onChange={e => onUpdate(bi, ei, 'load_pct_bw', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-load-pctbw-${bi}-${ei}`} />
                  </div>
                )}
              </>
            )}
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Rest (sec)</label>
              <NumericInput type="number" value={ex.rest_sec} onChange={e => onUpdate(bi, ei, 'rest_sec', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" data-testid={`input-rest-${bi}-${ei}`} />
            </div>
          </>
        )}
        {(blockType === 'RTP' || blockType === 'RECOVERY') && (
          <>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Sets</label>
              <NumericInput type="number" value={ex.sets} onChange={e => onUpdate(bi, ei, 'sets', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Reps</label>
              <NumericInput type="number" value={ex.reps} onChange={e => onUpdate(bi, ei, 'reps', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Duration (sec)</label>
              <NumericInput type="number" value={ex.duration_sec} onChange={e => onUpdate(bi, ei, 'duration_sec', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
            <div>
              <label className="text-[10px] text-zinc-500 block mb-0.5">Rest (sec)</label>
              <NumericInput type="number" value={ex.rest_sec} onChange={e => onUpdate(bi, ei, 'rest_sec', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
            </div>
          </>
        )}
      </div>
      <div className="mt-2">
        <NumericInput type="text" placeholder="Notes..." value={ex.notes} onChange={e => onUpdate(bi, ei, 'notes', e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 h-7 text-xs" />
      </div>

      {/* System Suggestion (exercise-filtered, non-destructive) — PLYOMETRIC only */}
      {blockType === 'PLYOMETRIC' && ex.exercise_name && focusAthleteId && (
        <SystemSuggestionCard
          bi={bi} ei={ei}
          exerciseName={ex.exercise_name}
          athleteId={focusAthleteId}
          onApply={(patch) => onUpdatePatch(bi, ei, patch)}
        />
      )}

      {/* Athlete-specific suggestions */}
      {ex.load_mode === 'PCT_BW' && <BodyWeightSuggestion athleteIds={athleteIds} percentage={ex.load_pct_bw}
        reference={weightReference} testId={`bw-suggestion-${testIdPrefix}-${bi}-${ei}`} />}
      {exSuggestions && exSuggestions.length > 0 && (
        <div className="mt-3 border-t border-zinc-700/50 pt-2" data-testid={`suggestions-${bi}-${ei}`}>
          <div className="flex items-center gap-1 mb-1.5">
            <Sparkles className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">Athlete Suggestions</span>
          </div>
          <div className="space-y-1 max-h-48 overflow-y-auto">
            {exSuggestions.map((s, si) => (
              <div key={si} className={`text-[11px] bg-zinc-800/50 rounded px-2 py-1.5 ${s.warning ? '' : ''}`} data-testid={`suggestion-row-${testIdPrefix}-${bi}-${ei}-${si}`}>
                {s.warning ? (
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-300 font-medium">{s.athlete_name || s.athlete_id}</span>
                    <span className="text-amber-400 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate max-w-[280px]">{s.warning}</span>
                    </span>
                  </div>
                ) : blockType === 'AEROBIC' && s.mas_kmh != null ? (
                  /* Enhanced aerobic display: MAS, distance per rep, total distance */
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-300 font-medium min-w-[100px]">{s.athlete_name || s.athlete_id}</span>
                    <div className="flex items-center gap-3 text-[11px]">
                      <span className="text-zinc-500">MAS: <span className="text-zinc-300">{s.mas_kmh} km/h</span></span>
                      <span className="text-emerald-400 font-medium">{s.target_distance_m}m/rep</span>
                      {s.total_distance_m != null && (
                        <span className="text-blue-400 font-medium">{s.total_distance_m}m total</span>
                      )}
                      <span className="text-zinc-500">{s.target_speed_kmh} km/h</span>
                    </div>
                  </div>
                ) : (
                  /* Strength / generic display */
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-300 font-medium min-w-[100px]">{s.athlete_name || s.athlete_id}</span>
                    <div className="flex items-center gap-3 flex-wrap justify-end">
                      {s.source && <Badge className="bg-zinc-700/50 text-zinc-400 text-[9px]">{s.source}</Badge>}
                      {s.suggested_load_min_kg != null && (
                        <span className="text-emerald-400 font-medium">
                          {s.suggested_load_min_kg}-{s.suggested_load_max_kg} kg
                        </span>
                      )}
                      {s.suggested_velocity_min != null && (
                        <span className="text-blue-400">
                          {s.suggested_velocity_min}-{s.suggested_velocity_max} m/s
                        </span>
                      )}
                      {s.target_speed_kmh != null && (
                        <span className="text-emerald-400">{s.target_speed_kmh} km/h</span>
                      )}
                      {s.target_distance_m != null && (
                        <span className="text-blue-400">{s.target_distance_m}m</span>
                      )}
                      {s.mas_kmh != null && (
                        <span className="text-zinc-500">MAS: {s.mas_kmh}</span>
                      )}
                      {s.estimated_1rm != null && (
                        <span className="text-zinc-500">e1RM: {s.estimated_1rm}kg</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
});

/* ======================================================
   Plyometric Per-Athlete Panel — exercise-specific, info-only
   - Filters jump metrics by each exercise's classification
   - Shows %BW × BW = kg per weighted exercise per athlete
   - NEVER auto-populates prescription
   - Shows "No <exercise> test data available" when relevant
     exercise-specific data is absent (no CMJ → SJ fallback)
   ====================================================== */

// Mirror of backend _classify_plyometric (keep small, pure)
function classifyPlyoFromName(name) {
  if (!name) return null;
  const n = String(name).trim().toLowerCase();
  if (n.includes('weighted') && (n.includes('cmj') || n.includes('countermovement'))) return 'CMJ_WEIGHTED';
  if (n.includes('weighted') && (n.includes('squat jump') || n.startsWith('sj '))) return 'SJ_WEIGHTED';
  if (n.includes('drop jump') || n.includes('depth jump') || n === 'dj') return 'DROP_JUMP';
  if (n.includes('squat jump') || n === 'sj') return 'SJ_INFO';
  if (n.includes('cmj') || n.includes('countermovement')) return 'CMJ_INFO';
  return null;
}

// Given a classification, return the required metric label(s) from backend jump payload
function metricsForClassification(cls, metrics) {
  if (!cls || !metrics) return { required: [], has: false };
  const cmj = metrics.cmj_height_cm;
  const sj = metrics.sj_height_cm;
  const rsi = metrics.dj_rsi;
  if (cls === 'CMJ_INFO' || cls === 'CMJ_WEIGHTED') {
    return { required: [{ label: 'CMJ', value: cmj != null ? `${cmj} cm` : null }], has: cmj != null };
  }
  if (cls === 'SJ_INFO' || cls === 'SJ_WEIGHTED') {
    return { required: [{ label: 'SJ', value: sj != null ? `${sj} cm` : null }], has: sj != null };
  }
  if (cls === 'DROP_JUMP') {
    return {
      required: [
        { label: 'RSI', value: rsi != null ? Number(rsi).toFixed(2) : null },
        { label: 'DJ Height', value: metrics.dj_height_cm != null ? `${metrics.dj_height_cm} cm` : null },
      ],
      has: rsi != null || metrics.dj_height_cm != null,
    };
  }
  return { required: [], has: false };
}

function missingDataLabel(cls) {
  if (cls === 'CMJ_INFO' || cls === 'CMJ_WEIGHTED') return 'No CMJ test data available';
  if (cls === 'SJ_INFO' || cls === 'SJ_WEIGHTED') return 'No Squat Jump test data available';
  if (cls === 'DROP_JUMP') return 'No Drop Jump test data available';
  return 'No relevant test data available';
}

const JumpSuggestionsPanel = React.memo(function JumpSuggestionsPanel({
  data, blockExercises, onPrescribedChange, onPrescribedSet,
}) {
  // Render only when we have metrics OR when there are exercises with classification
  const exercises = Array.isArray(blockExercises) ? blockExercises : [];
  const hasPlyoExercises = exercises.some(e => e.exercise_name && classifyPlyoFromName(e.exercise_name));
  if (!data || Object.keys(data).length === 0) return null;
  if (!hasPlyoExercises) {
    // No plyo exercises in the block yet — show only per-athlete overrides when helpful
    // but keep the panel hidden until coach adds exercises. This prevents showing CMJ/SJ/RSI
    // that are irrelevant to empty or non-plyo exercise selections.
    return null;
  }
  return (
    <div className="mt-3 border-t border-orange-500/20 pt-3" data-testid="jump-suggestions-panel">
      <div className="flex items-center gap-1 mb-1">
        <Sparkles className="w-3 h-3 text-orange-400" />
        <span className="text-[10px] font-semibold text-orange-400 uppercase tracking-wider">Athlete Data per Exercise (info only)</span>
      </div>
      <div className="text-[10px] text-zinc-500 italic mb-2">
        Prescription above is the source of truth — this panel never auto-fills it.
      </div>
      <div className="space-y-3 max-h-[640px] overflow-y-auto">
        {Object.entries(data).map(([aid, d]) => {
          const isRTP = (d.flags || []).includes('IN_RTP');
          return (
            <div key={aid} className={`rounded-sm p-3 border ${isRTP ? 'bg-rose-500/5 border-rose-500/30' : 'bg-zinc-800/60 border-zinc-700/50'}`} data-testid={`jump-athlete-${aid}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-zinc-200">{d.athlete_name || aid}</span>
                <div className="flex gap-1 flex-wrap">
                  {isRTP && (
                    <Badge className="text-[9px] bg-rose-500/20 text-rose-400 border-rose-500/30" data-testid={`flag-rtp-${aid}`}>RTP ATHLETE</Badge>
                  )}
                  {(d.flags || []).filter(f => f !== 'IN_RTP').map(flag => (
                    <Badge key={flag} className={`text-[9px] ${
                      flag === 'LOW_RSI' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                      flag === 'HIGH_ASYMMETRY' ? 'bg-purple-500/20 text-purple-400 border-purple-500/30' :
                      flag === 'PERF_DROP' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                      'bg-zinc-700/50 text-zinc-400'
                    }`} data-testid={`flag-${flag}-${aid}`}>{flag.replace(/_/g, ' ')}</Badge>
                  ))}
                </div>
              </div>

              {/* Per-exercise breakdown */}
              <div className="space-y-1.5 mb-2">
                {exercises.map((ex, idx) => {
                  const cls = classifyPlyoFromName(ex.exercise_name);
                  if (!cls) return null;
                  const { required, has } = metricsForClassification(cls, d.metrics);
                  const isWeighted = cls === 'CMJ_WEIGHTED' || cls === 'SJ_WEIGHTED';
                  const mode = (ex.load_mode || 'ABSOLUTE');
                  const pctBw = ex.load_pct_bw ? parseFloat(ex.load_pct_bw) : null;
                  const loadKg = ex.load_kg ? parseFloat(ex.load_kg) : null;
                  return (
                    <div key={idx} className="bg-zinc-900/50 rounded px-2 py-1.5 text-[11px]" data-testid={`plyo-ex-${aid}-${idx}`}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-zinc-200 font-medium">{ex.exercise_name}</span>
                        {/* Load display: %BW × BW = kg OR plain kg */}
                        {mode === 'PCT_BW' && pctBw ? (
                          <span className="text-zinc-400" data-testid={`plyo-ex-bw-${aid}-${idx}`}>{pctBw}%BW</span>
                        ) : loadKg != null ? (
                          <span className="text-emerald-400 font-medium">{loadKg} kg</span>
                        ) : null}
                      </div>
                      {/* Exercise-specific metric or "no data" message */}
                      <div className="flex flex-wrap gap-3 text-[10px]">
                        {isWeighted && !has && (
                          <span className="text-zinc-500 italic">Test data informational only — load from %BW above</span>
                        )}
                        {has ? (
                          required.map(m => (
                            <span key={m.label} className="text-zinc-400">
                              {m.label}: <span className="text-zinc-200">{m.value ?? '—'}</span>
                            </span>
                          ))
                        ) : (
                          !isWeighted && <span className="text-zinc-500 italic" data-testid={`plyo-ex-nodata-${aid}-${idx}`}>{missingDataLabel(cls)}</span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-athlete decision: Apply Suggestion vs Keep My Prescription */}
              {(() => {
                const sv = d.suggested_values;
                const pv = d.prescribed_values || {};
                const hasOverride = (
                  (pv.sets !== undefined && pv.sets !== null && pv.sets !== '') ||
                  (pv.reps !== undefined && pv.reps !== null && pv.reps !== '') ||
                  (pv.contacts !== undefined && pv.contacts !== null && pv.contacts !== '') ||
                  (pv.intensity_level !== undefined && pv.intensity_level !== null && pv.intensity_level !== '')
                );
                // Base values come from the first plyo exercise in the block (block-level prescription)
                const baseEx = exercises.find(e => classifyPlyoFromName(e.exercise_name)) || exercises[0] || {};
                const baseSets = baseEx.sets ?? '';
                const baseReps = baseEx.reps ?? '';
                const baseContacts = baseEx.contacts ?? '';
                const baseIntensity = baseEx.intensity_level ?? '';
                const usingSuggestion = hasOverride && sv && (
                  String(pv.sets) === String(sv.sets_min ?? sv.sets) &&
                  String(pv.reps) === String(sv.reps_min ?? sv.reps) &&
                  String(pv.contacts) === String(sv.contacts_min ?? sv.contacts) &&
                  String(pv.intensity_level) === String(sv.intensity_level)
                );

                const handleApply = () => {
                  if (!sv) return;
                  onPrescribedSet(aid, {
                    sets: sv.sets_min ?? sv.sets ?? '',
                    reps: sv.reps_min ?? sv.reps ?? '',
                    contacts: sv.contacts_min ?? sv.contacts ?? '',
                    intensity_level: sv.intensity_level ?? '',
                  });
                };
                const handleKeep = () => onPrescribedSet(aid, null);

                return (
                  <div className="mt-2 mb-2 bg-zinc-950/40 border border-zinc-700/40 rounded px-2 py-2" data-testid={`decision-${aid}`}>
                    {/* Status banner */}
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-1.5">
                        {hasOverride ? (
                          <Badge className="text-[9px] bg-cyan-500/20 text-cyan-300 border-cyan-500/40" data-testid={`status-suggestion-${aid}`}>
                            {usingSuggestion ? 'Using system suggestion' : 'Using custom override'}
                          </Badge>
                        ) : (
                          <Badge className="text-[9px] bg-emerald-500/20 text-emerald-300 border-emerald-500/40" data-testid={`status-coach-${aid}`}>
                            Using coach prescription
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {sv && (
                          <Button
                            size="sm"
                            type="button"
                            onClick={handleApply}
                            className="h-6 text-[10px] px-2 bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 border border-cyan-500/40"
                            data-testid={`apply-suggestion-${aid}`}
                          >Apply Suggestion</Button>
                        )}
                        <Button
                          size="sm"
                          type="button"
                          variant="outline"
                          onClick={handleKeep}
                          className="h-6 text-[10px] px-2 bg-zinc-800 hover:bg-zinc-700 border-zinc-600 text-zinc-300"
                          data-testid={`keep-prescription-${aid}`}
                        >Keep My Prescription</Button>
                      </div>
                    </div>
                    {/* Resolved values shown to coach */}
                    <div className="text-[10px] text-zinc-400 grid grid-cols-4 gap-1" data-testid={`resolved-${aid}`}>
                      <div>Sets: <span className="text-zinc-200">{(hasOverride ? pv.sets : baseSets) || '—'}</span></div>
                      <div>Reps: <span className="text-zinc-200">{(hasOverride ? pv.reps : baseReps) || '—'}</span></div>
                      <div>Contacts: <span className="text-zinc-200">{(hasOverride ? pv.contacts : baseContacts) || '—'}</span></div>
                      <div>Intensity: <span className="text-zinc-200">{(hasOverride ? pv.intensity_level : baseIntensity) || '—'}</span></div>
                    </div>
                    {sv && !hasOverride && (
                      <div className="text-[9px] text-zinc-500 italic mt-1" data-testid={`available-suggestion-${aid}`}>
                        Available suggestion: {sv.sets_min}-{sv.sets_max} sets · {sv.reps_min}-{sv.reps_max} reps · {sv.contacts_min}-{sv.contacts_max} contacts · {sv.intensity_level}
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* Per-athlete override (optional) — operates at block level */}
              <div className="mt-2 border-t border-zinc-800 pt-2">
                <span className="text-[10px] text-zinc-400 font-medium uppercase tracking-wider">Per-Athlete Override (optional)</span>
                <div className="text-[9px] text-zinc-500 mb-1">Manually set individual block targets. Leave empty to use team-level prescription.</div>
                <div className="grid grid-cols-4 gap-2">
                  <div>
                    <label className="text-[9px] text-zinc-400 block mb-0.5">Sets</label>
                    <NumericInput type="number" value={d.prescribed_values?.sets ?? ''} onChange={e => onPrescribedChange(aid, 'sets', e.target.value)} className="bg-zinc-900 border-zinc-600 text-zinc-100 h-7 text-xs" data-testid={`jump-rx-sets-${aid}`} />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-400 block mb-0.5">Reps</label>
                    <NumericInput type="number" value={d.prescribed_values?.reps ?? ''} onChange={e => onPrescribedChange(aid, 'reps', e.target.value)} className="bg-zinc-900 border-zinc-600 text-zinc-100 h-7 text-xs" data-testid={`jump-rx-reps-${aid}`} />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-400 block mb-0.5">Contacts</label>
                    <NumericInput type="number" value={d.prescribed_values?.contacts ?? ''} onChange={e => onPrescribedChange(aid, 'contacts', e.target.value)} className="bg-zinc-900 border-zinc-600 text-zinc-100 h-7 text-xs" data-testid={`jump-rx-contacts-${aid}`} />
                  </div>
                  <div>
                    <label className="text-[9px] text-zinc-400 block mb-0.5">Intensity</label>
                    <Select value={d.prescribed_values?.intensity_level || ''} onValueChange={v => onPrescribedChange(aid, 'intensity_level', v)}>
                      <SelectTrigger className="bg-zinc-900 border-zinc-600 text-zinc-100 h-7 text-xs" data-testid={`jump-rx-intensity-${aid}`}>
                        <SelectValue placeholder="\u2014" />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {INTENSITY_LEVELS.map(l => <SelectItem key={l} value={l} className="text-zinc-100 text-xs">{l}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

/* ======================================================
   BlockEditor — list of blocks with exercises
   Extracted to prevent re-mount on parent state change
   ====================================================== */
const BlockEditor = React.memo(function BlockEditor({
  blocksArr, onBlocksChange, exerciseLib, suggestions, onOpenExPicker, onJumpPrescribedChange, onJumpPrescribedSet, focusAthleteId, athleteIds, weightReference, testIdPrefix,
}) {
  const updateBlock = useCallback((idx, field, value) => {
    const updated = blocksArr.map((b, i) =>
      i === idx ? { ...b, [field]: value } : b
    );
    onBlocksChange(updated);
  }, [blocksArr, onBlocksChange]);

  const removeBlock = useCallback((idx) => {
    onBlocksChange(blocksArr.filter((_, i) => i !== idx));
  }, [blocksArr, onBlocksChange]);

  const addBlock = useCallback(() => {
    onBlocksChange([...blocksArr, emptyBlock()]);
  }, [blocksArr, onBlocksChange]);

  const updateExercise = useCallback((blockIdx, exIdx, field, value) => {
    const updated = blocksArr.map((b, bi) => {
      if (bi !== blockIdx) return b;
      const exercises = b.exercises.map((ex, ei) =>
        ei === exIdx ? { ...ex, [field]: value } : ex
      );
      return { ...b, exercises };
    });
    onBlocksChange(updated);
  }, [blocksArr, onBlocksChange]);

  // Multi-field exercise update — needed for changes that must mutate
  // several fields atomically (e.g. switching load_mode resets sibling load fields).
  // Calling updateExercise twice in a row uses a stale closure and the second call
  // overwrites the first.
  const updateExercisePatch = useCallback((blockIdx, exIdx, patch) => {
    const updated = blocksArr.map((b, bi) => {
      if (bi !== blockIdx) return b;
      const exercises = b.exercises.map((ex, ei) =>
        ei === exIdx ? { ...ex, ...patch } : ex
      );
      return { ...b, exercises };
    });
    onBlocksChange(updated);
  }, [blocksArr, onBlocksChange]);

  const removeExercise = useCallback((blockIdx, exIdx) => {
    const updated = blocksArr.map((b, bi) => {
      if (bi !== blockIdx) return b;
      return { ...b, exercises: b.exercises.filter((_, i) => i !== exIdx) };
    });
    onBlocksChange(updated);
  }, [blocksArr, onBlocksChange]);

  return (
    <div className="space-y-4">
      {blocksArr.map((block, bi) => (
        <div key={bi} className={`border rounded-sm p-4 ${CAT_COLORS[block.block_type] || 'border-zinc-700 bg-zinc-900/30'}`} data-testid={`block-${bi}`}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 flex-1">
              <NumericInput
                type="text"
                placeholder="Block name (e.g. Main Lifts)"
                value={block.name}
                onChange={e => updateBlock(bi, 'name', e.target.value)}
                className="bg-zinc-900/50 border-zinc-700 text-zinc-100 max-w-[200px] h-8 text-sm"
                data-testid={`block-name-${bi}`}
              />
              <Select value={block.block_type} onValueChange={v => updateBlock(bi, 'block_type', v)}>
                <SelectTrigger className="bg-zinc-900/50 border-zinc-700 text-zinc-100 w-[130px] h-8 text-xs" data-testid={`block-type-${bi}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {BLOCK_TYPES.map(t => <SelectItem key={t} value={t} className="text-zinc-100 text-xs">{t}</SelectItem>)}
                </SelectContent>
              </Select>
              {block.block_type === 'STRENGTH' && (
                <Select value={block.training_objective || ''} onValueChange={v => updateBlock(bi, 'training_objective', v)}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-700 text-zinc-100 w-[170px] h-8 text-xs" data-testid={`block-objective-${bi}`}>
                    <SelectValue placeholder="Training Objective" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {OBJECTIVES.map(o => <SelectItem key={o} value={o} className="text-zinc-100 text-xs">{OBJECTIVE_LABELS[o]}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
              {block.block_type === 'PLYOMETRIC' && (
                <Select value={block.jump_focus || ''} onValueChange={v => updateBlock(bi, 'jump_focus', v)}>
                  <SelectTrigger className="bg-zinc-900/50 border-zinc-700 text-zinc-100 w-[170px] h-8 text-xs" data-testid={`block-jump-focus-${bi}`}>
                    <SelectValue placeholder="Jump Focus" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    {JUMP_FOCUSES.map(f => <SelectItem key={f} value={f} className="text-zinc-100 text-xs">{JUMP_FOCUS_LABELS[f]}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => removeBlock(bi)} className="text-zinc-500 hover:text-rose-400 h-8" data-testid={`remove-block-${bi}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </Button>
          </div>

          {/* Exercises */}
          <div className="space-y-2">
            {block.exercises.map((ex, ei) => (
              <ExerciseRow
                key={`${bi}-${ei}-${ex.exercise_id}`}
                ex={ex} ei={ei} bi={bi}
                blockType={block.block_type}
                trainingObjective={block.training_objective}
                exerciseLib={exerciseLib}
                suggestions={suggestions}
                athleteIds={athleteIds}
                weightReference={weightReference}
                testIdPrefix={testIdPrefix}
                focusAthleteId={focusAthleteId}
                onUpdate={updateExercise}
                onUpdatePatch={updateExercisePatch}
                onRemove={removeExercise}
              />
            ))}
          </div>

          <Button
            variant="outline" size="sm"
            onClick={() => onOpenExPicker(bi)}
            className="mt-3 border-dashed border-zinc-700 text-zinc-400 hover:text-zinc-200 w-full"
            data-testid={`add-exercise-btn-${bi}`}
          >
            <Plus className="w-3.5 h-3.5 mr-1" /> Add Exercise
          </Button>

          <div className="mt-2">
            <NumericInput type="text" placeholder="Block notes..." value={block.notes} onChange={e => updateBlock(bi, 'notes', e.target.value)} className="bg-zinc-900/30 border-zinc-700 text-zinc-400 h-7 text-xs" />
          </div>
          {block.block_type === 'PLYOMETRIC' && block.jump_focus && suggestions[`jump_${block.jump_focus}`] && (
            <JumpSuggestionsPanel
              data={suggestions[`jump_${block.jump_focus}`]}
              blockExercises={block.exercises}
              onPrescribedChange={(athleteId, field, value) => onJumpPrescribedChange(block.jump_focus, athleteId, field, value)}
              onPrescribedSet={(athleteId, patch) => onJumpPrescribedSet(block.jump_focus, athleteId, patch)}
            />
          )}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addBlock} className="border-dashed border-zinc-700 text-zinc-400 hover:text-zinc-200" data-testid="add-block-btn">
        <Plus className="w-3.5 h-3.5 mr-1" /> Add Block
      </Button>
    </div>
  );
});


/* ======================================================
   Main Builder Page
   ====================================================== */
export default function PrescriptionBuilderPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const templateData = location.state?.template;
  const editMicrocycle = location.state?.editMicrocycle;

  const [exerciseLib, setExerciseLib] = useState([]);
  const [teams, setTeams] = useState([]);
  const [athletes, setAthletes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showExPicker, setShowExPicker] = useState(null); // { blockIdx, target: 'main' | dayOfWeek }
  const [suggestions, setSuggestions] = useState({});
  const [rtpWarnings, setRtpWarnings] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Form state
  const [title, setTitle] = useState('');
  const [rxType, setRxType] = useState('SINGLE_SESSION');
  const [category, setCategory] = useState('STRENGTH');
  const [targetType, setTargetType] = useState('TEAM');
  const [targetTeamId, setTargetTeamId] = useState('');
  const [targetAthleteIds, setTargetAthleteIds] = useState([]);
  const [sessionId, setSessionId] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [weekStart, setWeekStart] = useState('');
  const [notes, setNotes] = useState('');
  const [blocks, setBlocks] = useState([emptyBlock()]);
  const [gymClassification, setGymClassification] = useState(null);
  const [microcycleDays, setMicrocycleDays] = useState([]);
  // Phase 2: canonical-integration form fields
  const [durationPlanned, setDurationPlanned] = useState('');
  const [startTime, setStartTime] = useState('');
  const [rtpProfileId, setRtpProfileId] = useState('');
  const [availableRtpProfiles, setAvailableRtpProfiles] = useState([]);
  const isEditMode = !!editMicrocycle;

  // Load initial data
  useEffect(() => {
    const load = async () => {
      try {
        const [exRes, teamRes, athRes] = await Promise.all([
          api.get('/exercises'),
          api.get('/teams'),
          api.get('/athletes'),
        ]);
        setExerciseLib(exRes.data);
        setTeams(teamRes.data);
        setAthletes(athRes.data);
      } catch (e) {
        toast.error('Failed to load data');
      }
    };
    load();
  }, [api]);

  // Apply template
  useEffect(() => {
    if (templateData) {
      setTitle(templateData.name || '');
      setCategory(templateData.category || 'STRENGTH');
      const tplBlocks = (templateData.blocks || []).map(b => ({
        name: b.name, block_type: b.block_type, training_objective: '',
        jump_focus: '',
        notes: b.notes || '',
        exercises: (b.items || []).map(item => ({
          exercise_id: item.exercise_id, exercise_name: item.exercise_name || '',
          sets: item.sets ?? '', reps: item.reps ?? '',
          load_kg: '', load_pct: item.load_pct ?? '',
          velocity_target: item.velocity_target ?? '',
          duration_sec: item.duration_sec ?? '',
          work_sec: item.params?.work_sec ?? '',
          rest_sec: item.params?.rest_sec ?? item.rest_sec ?? '',
          intensity_pct: item.params?.intensity_pct ?? item.intensity_pct ?? '',
          distance_m: '', target_speed_kmh: '',
          notes: item.notes || '',
          contacts: '', intensity_level: '',
          is_vbt_linked: false, vbt_lift_type: null,
        })),
      }));
      setBlocks(tplBlocks.length > 0 ? tplBlocks : [emptyBlock()]);
    }
  }, [templateData]);

  // Pre-populate for edit mode
  useEffect(() => {
    if (editMicrocycle) {
      setTitle(editMicrocycle.title || '');
      setRxType('MICROCYCLE');
      setCategory(editMicrocycle.category || 'STRENGTH');
      setTargetType(editMicrocycle.target_type || 'TEAM');
      setTargetTeamId(editMicrocycle.target_team_id || '');
      setTargetAthleteIds(editMicrocycle.target_athlete_ids || []);
      setWeekStart(editMicrocycle.week_start || '');
      setNotes(editMicrocycle.notes || '');
      setSuggestions(editMicrocycle.athlete_data || {});
      const days = (editMicrocycle.microcycle_days || []).map(d => ({
        day_of_week: d.day_of_week,
        day_label: d.day_label || DAY_LABELS[d.day_of_week],
        session_id: d.session_id || null,
        notes: d.notes || '',
        // Phase 2 canonical fields
        day_category: d.day_category || '',
        duration_planned_min: d.duration_planned_min || '',
        start_time: d.start_time || '',
        title: d.title || '',
        blocks: (d.blocks || []).map(b => ({
          name: b.name || '', block_type: b.block_type || 'STRENGTH',
          training_objective: b.training_objective || '',
          jump_focus: b.jump_focus || '',
          notes: b.notes || '',
          exercises: (b.exercises || []).map(ex => ({
            exercise_id: ex.exercise_id || '', exercise_name: ex.exercise_name || '',
            sets: ex.sets ?? '', reps: ex.reps ?? '',
            load_mode: ex.load_mode ?? 'ABSOLUTE', load_pct_bw: ex.load_pct_bw ?? '',
            load_kg: ex.load_kg ?? '', load_pct: ex.load_pct ?? '',
            velocity_target: ex.velocity_target ?? '',
            duration_sec: ex.duration_sec ?? '',
            work_sec: ex.work_sec ?? '', rest_sec: ex.rest_sec ?? '',
            intensity_pct: ex.intensity_pct ?? '',
            distance_m: ex.distance_m ?? '', target_speed_kmh: ex.target_speed_kmh ?? '',
            notes: ex.notes || '',
            contacts: ex.contacts ?? '', intensity_level: ex.intensity_level ?? '',
            is_vbt_linked: false, vbt_lift_type: null,
          })),
        })),
      }));
      setMicrocycleDays(days);
    }
  }, [editMicrocycle]);

  // FIX: team_ids → teams[].team_id
  const teamAthletes = useMemo(() => {
    if (targetType === 'TEAM' && targetTeamId) {
      return athletes.filter(a =>
        (a.teams || []).some(t => t.team_id === targetTeamId)
      );
    }
    return athletes;
  }, [targetType, targetTeamId, athletes]);

  const selectedAthleteIds = useMemo(() => {
    if (targetType === 'TEAM' && targetTeamId) {
      return teamAthletes.map(a => a.id);
    }
    return targetAthleteIds;
  }, [targetType, targetTeamId, teamAthletes, targetAthleteIds]);

  // Fetch suggestions
  const fetchSuggestions = useCallback(async () => {
    if (selectedAthleteIds.length === 0) {
      toast.error('Select athletes first');
      return;
    }
    setLoadingSuggestions(true);
    const newSuggestions = {};
    const allBlocks = rxType === 'MICROCYCLE'
      ? microcycleDays.flatMap(d => d.blocks)
      : blocks;

    try {
      // RTP warnings for all selected athletes
      const rtpRes = await api.post('/prescriptions/suggestions/rtp', selectedAthleteIds);
      setRtpWarnings(rtpRes.data || []);

      for (const block of allBlocks) {
        for (const ex of block.exercises) {
          const exLib = exerciseLib.find(e => e.id === ex.exercise_id);

          // VBT-linked strength exercise
          if (block.block_type === 'STRENGTH' && exLib?.is_vbt_linked && block.training_objective) {
            try {
              const res = await api.post('/prescriptions/suggestions/strength', {
                exercise_id: ex.exercise_id,
                training_objective: block.training_objective,
                athlete_ids: selectedAthleteIds,
              });
              const key = `${ex.exercise_id}_${block.training_objective}`;
              newSuggestions[key] = res.data;
            } catch (e) { /* silent */ }
          }

          // Aerobic exercise with work_sec and intensity
          if (block.block_type === 'AEROBIC' && ex.work_sec && ex.intensity_pct) {
            try {
              const res = await api.post('/prescriptions/suggestions/aerobic', {
                work_sec: parseInt(ex.work_sec),
                intensity_pct: parseFloat(ex.intensity_pct),
                sets: parseInt(ex.sets) || 1,
                reps: parseInt(ex.reps) || 1,
                athlete_ids: selectedAthleteIds,
              });
              const key = prescriptionSuggestionKey(block, ex);
              newSuggestions[key] = res.data;
            } catch (e) { /* silent */ }
          }
        }
      }
      // Jump / Plyometric suggestions
      const plyoFocuses = new Set();
      for (const block of allBlocks) {
        if (block.block_type === 'PLYOMETRIC' && block.jump_focus) {
          plyoFocuses.add(block.jump_focus);
        }
      }
      for (const focus of plyoFocuses) {
        try {
          const res = await api.post('/prescriptions/suggestions/jump', {
            focus,
            athlete_ids: selectedAthleteIds,
          });
          const jumpData = {};
          for (const [aid, s] of Object.entries(res.data)) {
            jumpData[aid] = {
              athlete_id: aid,
              athlete_name: s.athlete_name,
              source: s.source,
              warning: s.warning,
              flags: s.flags || [],
              metrics: {
                cmj_height_cm: s.cmj_height_cm,
                sj_height_cm: s.sj_height_cm,
                dj_rsi: s.dj_rsi,
                dj_height_cm: s.dj_height_cm,
                dj_contact_time_ms: s.dj_contact_time_ms,
                asymmetry_pct: s.asymmetry_pct,
              },
              suggested_values: {
                exercise_type: s.suggested_exercise_type,
                sets_min: s.suggested_sets_min,
                sets_max: s.suggested_sets_max,
                reps_min: s.suggested_reps_min,
                reps_max: s.suggested_reps_max,
                contacts_min: s.suggested_contacts_min,
                contacts_max: s.suggested_contacts_max,
                intensity_level: s.suggested_intensity_level,
              },
              prescribed_values: {}, // start empty — coach prescription is source of truth
            };
          }
          newSuggestions[`jump_${focus}`] = jumpData;
        } catch (e) { /* silent */ }
      }
      setSuggestions(newSuggestions);
      toast.success(`Suggestions loaded for ${selectedAthleteIds.length} athlete(s)`);
    } catch (e) {
      toast.error('Failed to fetch suggestions');
    } finally {
      setLoadingSuggestions(false);
    }
  }, [api, blocks, microcycleDays, rxType, selectedAthleteIds, exerciseLib]);

  // Microcycle day management
  const addMicrocycleDay = (dow) => {
    if (microcycleDays.some(d => d.day_of_week === dow)) return;
    setMicrocycleDays(prev => [...prev, emptyDay(dow)].sort((a, b) => a.day_of_week - b.day_of_week));
  };
  const removeMicrocycleDay = (dow) => {
    setMicrocycleDays(prev => prev.filter(d => d.day_of_week !== dow));
  };
  const updateDayBlocks = useCallback((dow, newBlocks) => {
    setMicrocycleDays(prev => prev.map(d =>
      d.day_of_week === dow ? { ...d, blocks: newBlocks } : d
    ));
  }, []);
  const updateDayField = useCallback((dow, field, value) => {
    setMicrocycleDays(prev => prev.map(d =>
      d.day_of_week === dow ? { ...d, [field]: value } : d
    ));
  }, []);

  // Jump prescribed value override (single field)
  const handleJumpPrescribedChange = useCallback((focus, athleteId, field, value) => {
    setSuggestions(prev => {
      const key = `jump_${focus}`;
      const jumpData = { ...(prev[key] || {}) };
      if (jumpData[athleteId]) {
        jumpData[athleteId] = {
          ...jumpData[athleteId],
          prescribed_values: {
            ...(jumpData[athleteId].prescribed_values || {}),
            [field]: value,
          },
        };
      }
      return { ...prev, [key]: jumpData };
    });
  }, []);

  // Jump prescribed value override (replace whole patch atomically).
  // Used by per-athlete "Apply Suggestion" (patch=full suggested values) and
  // "Keep My Prescription" (patch=null → clears overrides for that athlete).
  const handleJumpPrescribedSet = useCallback((focus, athleteId, patch) => {
    setSuggestions(prev => {
      const key = `jump_${focus}`;
      const jumpData = { ...(prev[key] || {}) };
      if (jumpData[athleteId]) {
        jumpData[athleteId] = {
          ...jumpData[athleteId],
          prescribed_values: patch || {},
        };
      }
      return { ...prev, [key]: jumpData };
    });
  }, []);

  // Fetch active RTP profiles when category=RTP + single-athlete target
  useEffect(() => {
    let cancelled = false;
    async function fetchProfiles() {
      if (category !== 'RTP') {
        setAvailableRtpProfiles([]);
        return;
      }
      if (targetType !== 'ATHLETE' || targetAthleteIds.length !== 1) {
        setAvailableRtpProfiles([]);
        return;
      }
      try {
        const res = await api.get('/rtp/profiles', {
          params: { athlete_id: targetAthleteIds[0], status: 'ACTIVE' },
        });
        const active = (res.data || []).filter(p => p.status === 'ACTIVE');
        if (!cancelled) setAvailableRtpProfiles(active);
      } catch {
        if (!cancelled) setAvailableRtpProfiles([]);
      }
    }
    fetchProfiles();
    return () => { cancelled = true; };
  }, [api, category, targetType, targetAthleteIds]);

  // Save
  const handleSave = async (mode = 'ASSIGN') => {
    // mode = 'ASSIGN' (schedule + create canonical sessions) | 'TEMPLATE' (no scheduling)
    if (!title.trim()) { toast.error('Title is required / Titolo obbligatorio'); return; }
    if (targetType === 'TEAM' && !targetTeamId) { toast.error('Select a team / Seleziona una squadra'); return; }
    if (targetType !== 'TEAM' && targetAthleteIds.length === 0) { toast.error('Select athletes / Seleziona atleti'); return; }

    // Phase 2 validation (only for ASSIGN mode)
    if (mode === 'ASSIGN') {
      if (rxType === 'SINGLE_SESSION') {
        if (!date) { toast.error('Date required / Data obbligatoria'); return; }
        if (category === 'MIXED') {
          toast.error('MIXED category not allowed for a single session — pick a concrete category');
          return;
        }
        const dp = parseInt(durationPlanned);
        if (!dp || dp <= 0) { toast.error('Planned duration required (>0 min) / Durata programmata obbligatoria'); return; }
        if (category === 'RTP') {
          if (targetType !== 'ATHLETE' || targetAthleteIds.length !== 1) {
            toast.error('RTP requires exactly one target athlete / RTP richiede un solo atleta');
            return;
          }
          if (!rtpProfileId) {
            toast.error('Select an active RTP profile / Seleziona un profilo RTP attivo');
            return;
          }
        }
      } else {
        // MICROCYCLE
        if (!weekStart) { toast.error('Week start required / Settimana obbligatoria'); return; }
        if (microcycleDays.length === 0) { toast.error('Select at least one training day'); return; }
        for (const d of microcycleDays) {
          const dp = parseInt(d.duration_planned_min);
          if (!dp || dp <= 0) {
            toast.error(`${d.day_label}: planned duration required / durata obbligatoria`);
            return;
          }
          if (category === 'MIXED' && !d.day_category) {
            toast.error(`${d.day_label}: choose a concrete category / scegli una categoria`);
            return;
          }
          const dc = (d.day_category || '').toUpperCase();
          if (dc === 'RTP') {
            if (targetType !== 'ATHLETE' || targetAthleteIds.length !== 1) {
              toast.error(`${d.day_label}: RTP day requires exactly one target athlete`);
              return;
            }
            if (!rtpProfileId) {
              toast.error(`${d.day_label}: select an RTP profile`);
              return;
            }
          }
        }
      }
    }

    const toNum = (v, fn) => v !== '' && v != null ? fn(v) : null;

    const cleanExercise = (ex) => ({
      exercise_id: ex.exercise_id, exercise_name: ex.exercise_name,
      sets: toNum(ex.sets, parseInt), reps: toNum(ex.reps, parseInt),
      load_mode: ex.load_mode || 'ABSOLUTE',
      load_kg: toNum(ex.load_kg, parseFloat), load_pct: toNum(ex.load_pct, parseFloat),
      load_pct_bw: toNum(ex.load_pct_bw, parseFloat),
      velocity_target: toNum(ex.velocity_target, parseFloat),
      duration_sec: toNum(ex.duration_sec, parseInt),
      work_sec: toNum(ex.work_sec, parseInt), rest_sec: toNum(ex.rest_sec, parseInt),
      intensity_pct: toNum(ex.intensity_pct, parseFloat),
      distance_m: toNum(ex.distance_m, parseFloat),
      target_speed_kmh: toNum(ex.target_speed_kmh, parseFloat),
      notes: ex.notes || null,
      contacts: toNum(ex.contacts, parseInt),
      intensity_level: ex.intensity_level || null,
    });

    setSaving(true);
    try {
      const isTemplate = mode === 'TEMPLATE';
      const payload = {
        title, prescription_type: rxType, category, target_type: targetType,
        target_athlete_ids: targetType === 'TEAM' ? [] : targetAthleteIds,
        target_team_id: targetType === 'TEAM' ? targetTeamId : null,
        session_id: sessionId || null,
        date: (!isTemplate && rxType === 'SINGLE_SESSION') ? date : null,
        week_start: (!isTemplate && rxType === 'MICROCYCLE') ? weekStart : null,
        // Phase 2 canonical fields
        duration_planned_min: (!isTemplate && rxType === 'SINGLE_SESSION') ? parseInt(durationPlanned) : null,
        gym_classification: !isTemplate && rxType === 'SINGLE_SESSION' && category === 'STRENGTH' ? gymClassification : null,
        start_time: (!isTemplate && rxType === 'SINGLE_SESSION') ? (startTime || null) : null,
        rtp_profile_id: (!isTemplate && category === 'RTP') ? (rtpProfileId || null) : null,
        save_as_template: isTemplate,
        blocks: rxType === 'SINGLE_SESSION' ? blocks.map(b => ({
          name: b.name, block_type: b.block_type, training_objective: b.training_objective || null,
          jump_focus: b.jump_focus || null,
          exercises: b.exercises.map(cleanExercise), notes: b.notes || null,
        })) : [],
        microcycle_days: rxType === 'MICROCYCLE' ? microcycleDays.map(d => ({
          day_of_week: d.day_of_week, day_label: d.day_label,
          day_category: d.day_category || null,
          duration_planned_min: toNum(d.duration_planned_min, parseInt),
          ...(!isTemplate && !isEditMode && (d.day_category || category) === 'STRENGTH' ? { gym_classification: d.gym_classification ?? null } : {}),
          start_time: d.start_time || null,
          title: d.title || null,
          blocks: d.blocks.map(b => ({
            name: b.name, block_type: b.block_type, training_objective: b.training_objective || null,
            jump_focus: b.jump_focus || null,
            exercises: b.exercises.map(cleanExercise), notes: b.notes || null,
          })),
          session_id: d.session_id || null, notes: d.notes || null,
        })) : [],
        notes: notes || null,
        athlete_data: suggestions,
      };

      let res;
      if (isEditMode) {
        // Edit mode: use PUT microcycle endpoint (Assign & schedule flow)
        res = await api.put(`/prescriptions/microcycles/${editMicrocycle.id}`, payload);
        toast.success('Microcycle updated / Microciclo aggiornato');
      } else {
        res = await api.post('/prescriptions', payload);
        toast.success(isTemplate
          ? 'Template saved / Modello salvato'
          : 'Prescription assigned & scheduled / Prescrizione assegnata');
      }
      navigate(`/staff/prescriptions/${res.data.id}`);
    } catch (e) {
      toast.error(gymValidationMessage(e, 'Failed to save prescription'));
    } finally {
      setSaving(false);
    }
  };

  // Exercise picker filtered — now handled by ExerciseSelector component
  const handleExerciseSelected = useCallback((exData) => {
    if (!showExPicker) return;
    const newEx = emptyExercise(exData);
    const { blockIdx, target } = showExPicker;
    if (target === 'main') {
      setBlocks(prev => prev.map((b, i) =>
        i === blockIdx ? { ...b, exercises: [...b.exercises, newEx] } : b
      ));
    } else {
      setMicrocycleDays(prev => prev.map(d => {
        if (d.day_of_week !== target) return d;
        return { ...d, blocks: d.blocks.map((b, i) =>
          i === blockIdx ? { ...b, exercises: [...b.exercises, newEx] } : b
        )};
      }));
    }
    setShowExPicker(null);
  }, [showExPicker]);

  // Main-blocks exercise picker callback
  const handleOpenExPickerMain = useCallback((blockIdx) => {
    setShowExPicker({ blockIdx, target: 'main' });
  }, []);

  return (
    <div className="space-y-6 min-w-0 overflow-x-hidden" data-testid="prescription-builder-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/staff/prescriptions')} className="text-zinc-400 hover:text-zinc-100" data-testid="back-to-prescriptions">
          <ChevronLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <h1 className="text-2xl font-bold text-zinc-100" data-testid="builder-title">{isEditMode ? 'Edit Microcycle' : 'Prescription Builder'}</h1>
      </div>

      {/* RTP Warnings */}
      {rtpWarnings.length > 0 && (
        <div className="space-y-2" data-testid="rtp-warnings">
          {rtpWarnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-sm">
              <AlertTriangle className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div>
                <span className="text-sm font-medium text-amber-300">{w.athlete_name}</span>
                <span className="text-xs text-amber-400/80 ml-2">{w.warning}</span>
                {w.active_caps?.length > 0 && (
                  <div className="flex gap-2 mt-1">
                    {w.active_caps.map((c, ci) => (
                      <Badge key={ci} className="bg-amber-500/10 text-amber-400 text-[9px]">{c.metric}: max {c.weekly_cap}</Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 min-w-0">
        {/* Left: Settings */}
        <div className="space-y-4 min-w-0">
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4 space-y-4">
              <div>
                <Label className="text-zinc-400 text-xs">Title</Label>
                <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. Week 10 - Lower Body" className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="rx-title-input" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-zinc-400 text-xs">Type</Label>
                  <Select value={rxType} onValueChange={setRxType}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="rx-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      <SelectItem value="SINGLE_SESSION" className="text-zinc-100">Single Session</SelectItem>
                      <SelectItem value="MICROCYCLE" className="text-zinc-100">Weekly Microcycle</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs">Category</Label>
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="rx-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {CATEGORIES.map(c => <SelectItem key={c} value={c} className="text-zinc-100">{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {rxType === 'SINGLE_SESSION' && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 min-w-0">
                  <div>
                    <Label className="text-zinc-400 text-xs">Date / Data</Label>
                    <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid="rx-date-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Planned duration / Durata programmata (min)</Label>
                    <Input type="number" min="1" value={durationPlanned} onChange={e => setDurationPlanned(e.target.value)} placeholder="e.g. 60" className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid="rx-duration-input" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs">Start time / Ora inizio</Label>
                    <Input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid="rx-start-time-input" />
                  </div>
                </div>
              )}
              {rxType === 'MICROCYCLE' && (
                <div>
                  <Label className="text-zinc-400 text-xs">Week Start / Inizio settimana</Label>
                  <Input type="date" value={weekStart} onChange={e => setWeekStart(e.target.value)} className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid="rx-week-start-input" />
                </div>
              )}
              {category === 'RTP' && targetType === 'ATHLETE' && targetAthleteIds.length === 1 && (
                <div data-testid="rtp-profile-picker-wrap">
                  <Label className="text-zinc-400 text-xs">Active RTP profile / Profilo RTP attivo</Label>
                  {availableRtpProfiles.length === 0 ? (
                    <div className="text-xs text-red-400 mt-1 flex items-center gap-1" data-testid="rtp-no-profile-warning">
                      <AlertTriangle className="w-3 h-3" />
                      No active RTP profile for this athlete / Nessun profilo RTP attivo per questo atleta
                    </div>
                  ) : (
                    <Select value={rtpProfileId} onValueChange={setRtpProfileId}>
                      <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid="rtp-profile-select"><SelectValue placeholder="Select profile / Seleziona profilo" /></SelectTrigger>
                      <SelectContent className="bg-zinc-800 border-zinc-700">
                        {availableRtpProfiles.map(p => (
                          <SelectItem key={p.id} value={p.id} className="text-zinc-100" data-testid={`rtp-profile-option-${p.id}`}>
                            {p.injury_type || 'RTP'} — {p.current_phase || 'Phase ?'} ({p.status})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
              <div>
                <Label className="text-zinc-400 text-xs">Notes</Label>
                <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="rx-notes-input" />
              </div>
            </CardContent>
          </Card>

          {/* Target selection */}
          <Card className="bg-zinc-900/50 border-zinc-800">
            <CardContent className="p-4 space-y-4">
              <Label className="text-zinc-300 font-semibold text-xs uppercase tracking-wider">Target</Label>
              <div className="flex gap-1.5">
                {['TEAM', 'GROUP', 'ATHLETE'].map(t => (
                  <button key={t} onClick={() => setTargetType(t)}
                    className={`flex-1 px-3 py-2 text-xs font-medium rounded-sm border transition-colors ${
                      targetType === t ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-zinc-800 text-zinc-400 border-zinc-700 hover:border-zinc-600'
                    }`} data-testid={`target-${t.toLowerCase()}-btn`}>
                    {t === 'TEAM' && <Users className="w-3.5 h-3.5 inline mr-1" />}
                    {t === 'GROUP' && <Users className="w-3.5 h-3.5 inline mr-1" />}
                    {t === 'ATHLETE' && <User className="w-3.5 h-3.5 inline mr-1" />}
                    {t}
                  </button>
                ))}
              </div>

              {targetType === 'TEAM' && (
                <div>
                  <Select value={targetTeamId} onValueChange={setTargetTeamId}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100" data-testid="target-team-select"><SelectValue placeholder="Select team" /></SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {teams.map(t => (
                        <SelectItem key={t.id} value={t.id} className="text-zinc-100">{t.code} - {t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {targetTeamId && (
                    <div className="mt-2 text-xs text-zinc-500">
                      {teamAthletes.length} athlete(s) in team
                    </div>
                  )}
                </div>
              )}

              {(targetType === 'ATHLETE' || targetType === 'GROUP') && (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {athletes.map(a => (
                    <label key={a.id} className="flex items-center gap-2 px-2 py-1 hover:bg-zinc-800/30 rounded cursor-pointer" data-testid={`athlete-select-${a.id}`}>
                      <input type="checkbox" checked={targetAthleteIds.includes(a.id)}
                        onChange={e => {
                          if (e.target.checked) setTargetAthleteIds(prev => [...prev, a.id]);
                          else setTargetAthleteIds(prev => prev.filter(id => id !== a.id));
                        }} className="rounded border-zinc-600" />
                      <span className="text-xs text-zinc-300">{a.first_name} {a.last_name}</span>
                      {a.primary_position && <Badge variant="outline" className="text-[9px] border-zinc-700 text-zinc-500">{a.primary_position}</Badge>}
                    </label>
                  ))}
                </div>
              )}

              {selectedAthleteIds.length > 0 && (
                <div className="text-xs text-emerald-400 font-medium">{selectedAthleteIds.length} athlete(s) selected</div>
              )}
            </CardContent>
          </Card>

          {/* Actions — split into template vs assign & schedule */}
          <div className="space-y-2 relative z-10">
            <Button onClick={fetchSuggestions} disabled={loadingSuggestions || selectedAthleteIds.length === 0}
              variant="outline" className="w-full border-amber-500/30 text-amber-400 hover:bg-amber-500/10 min-h-[44px]" data-testid="fetch-suggestions-btn">
              {loadingSuggestions ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {loadingSuggestions ? 'Loading Suggestions...' : 'Get Athlete Suggestions'}
            </Button>
            {isEditMode ? (
              <Button onClick={() => handleSave('ASSIGN')} disabled={saving} className="w-full bg-amber-500 hover:bg-amber-600 text-black min-h-[44px]" data-testid="save-prescription-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Saving...' : 'Update Microcycle / Aggiorna microciclo'}
              </Button>
            ) : (
              <div className="flex flex-col gap-2 min-w-0">
                <Button onClick={() => handleSave('TEMPLATE')} disabled={saving}
                  variant="outline"
                  className="w-full border-zinc-600 text-zinc-200 hover:bg-zinc-800 min-h-[44px] whitespace-normal text-xs sm:text-sm"
                  data-testid="save-template-btn">
                  <Save className="w-4 h-4 mr-2 shrink-0" />
                  Save as template / Salva come modello
                </Button>
                <Button onClick={() => handleSave('ASSIGN')} disabled={saving}
                  className="w-full bg-amber-500 hover:bg-amber-600 text-black min-h-[44px] whitespace-normal text-xs sm:text-sm"
                  data-testid="save-prescription-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <Save className="w-4 h-4 mr-2 shrink-0" />}
                  Assign & schedule / Assegna e pianifica
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Right: Blocks editor */}
        <div className="lg:col-span-2 min-w-0">
          {rxType === 'SINGLE_SESSION' ? (
            <div>
              <h2 className="text-sm font-semibold text-zinc-300 mb-3 uppercase tracking-wider">Session Blocks</h2>
              {category === 'STRENGTH' && <GymClassificationEditor value={gymClassification}
                onChange={setGymClassification} duration={durationPlanned} prefix="prescription-create-gym" />}
              <BlockEditor
                blocksArr={blocks}
                onBlocksChange={setBlocks}
                exerciseLib={exerciseLib}
                suggestions={suggestions}
                focusAthleteId={selectedAthleteIds.length === 1 ? selectedAthleteIds[0] : null}
                athleteIds={selectedAthleteIds}
                weightReference={{ as_of: date }}
                testIdPrefix="single"
                onOpenExPicker={handleOpenExPickerMain}
                onJumpPrescribedChange={handleJumpPrescribedChange}
                onJumpPrescribedSet={handleJumpPrescribedSet}
              />
            </div>
          ) : (
            <div>
              <h2 className="text-sm font-semibold text-zinc-300 mb-3 uppercase tracking-wider">Weekly Microcycle</h2>
              <div className="flex gap-1.5 mb-4 flex-wrap">
                {DAY_LABELS.map((label, dow) => {
                  const hasDay = microcycleDays.some(d => d.day_of_week === dow);
                  return (
                    <button key={dow}
                      onClick={() => hasDay ? removeMicrocycleDay(dow) : addMicrocycleDay(dow)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                        hasDay ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' : 'bg-zinc-800 text-zinc-500 border-zinc-700 hover:border-zinc-600'
                      }`} data-testid={`day-toggle-${dow}`}>
                      {label.slice(0, 3)}
                    </button>
                  );
                })}
              </div>

              {microcycleDays.length === 0 ? (
                <div className="text-center py-8 text-zinc-500 border border-dashed border-zinc-700 rounded-sm">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-zinc-600" />
                  <p className="text-sm">Select training days above</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {microcycleDays.map(day => (
                    <div key={day.day_of_week} data-testid={`microcycle-day-${day.day_of_week}`}>
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="text-sm font-semibold text-amber-400">{day.day_label}</h3>
                        <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-500">
                          {day.blocks.length} block(s)
                        </Badge>
                      </div>
                      {/* Phase 2 canonical-integration per-day fields */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 mb-3 min-w-0">
                        {category === 'MIXED' && (
                          <div>
                            <Label className="text-zinc-500 text-[10px] uppercase">Category / Categoria *</Label>
                            <Select value={day.day_category || ''} onValueChange={(v) => updateDayField(day.day_of_week, 'day_category', v)}>
                              <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]" data-testid={`day-category-${day.day_of_week}`}>
                                <SelectValue placeholder="Choose / Scegli" />
                              </SelectTrigger>
                              <SelectContent className="bg-zinc-800 border-zinc-700">
                                {DAY_CATEGORIES.map(c => (
                                  <SelectItem key={c} value={c} className="text-zinc-100" data-testid={`day-category-${day.day_of_week}-opt-${c}`}>
                                    {DAY_CATEGORY_LABELS[c].en} / {DAY_CATEGORY_LABELS[c].it}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        )}
                        <div>
                          <Label className="text-zinc-500 text-[10px] uppercase">Duration min / Durata *</Label>
                          <Input type="number" min="1"
                            value={day.duration_planned_min || ''}
                            onChange={e => updateDayField(day.day_of_week, 'duration_planned_min', e.target.value)}
                            placeholder="e.g. 60"
                            className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]"
                            data-testid={`day-duration-${day.day_of_week}`} />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px] uppercase">Start time / Ora</Label>
                          <Input type="time"
                            value={day.start_time || ''}
                            onChange={e => updateDayField(day.day_of_week, 'start_time', e.target.value)}
                            className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]"
                            data-testid={`day-start-time-${day.day_of_week}`} />
                        </div>
                        <div>
                          <Label className="text-zinc-500 text-[10px] uppercase">Title / Titolo</Label>
                          <Input type="text"
                            value={day.title || ''}
                            onChange={e => updateDayField(day.day_of_week, 'title', e.target.value)}
                            placeholder={day.day_label}
                            className="bg-zinc-800 border-zinc-700 text-zinc-100 min-h-[44px]"
                            data-testid={`day-title-${day.day_of_week}`} />
                        </div>
                      </div>
                      {!isEditMode && (day.day_category || category) === 'STRENGTH' && <GymClassificationEditor
                        value={day.gym_classification ?? null} onChange={value => updateDayField(day.day_of_week, 'gym_classification', value)}
                        duration={day.duration_planned_min} prefix={`prescription-day-${day.day_of_week}-gym`} />}
                      <BlockEditor
                        blocksArr={day.blocks}
                        onBlocksChange={(newBlocks) => updateDayBlocks(day.day_of_week, newBlocks)}
                        exerciseLib={exerciseLib}
                        suggestions={suggestions}
                        focusAthleteId={selectedAthleteIds.length === 1 ? selectedAthleteIds[0] : null}
                        athleteIds={selectedAthleteIds}
                        weightReference={{ week_start: weekStart, day_of_week: day.day_of_week }}
                        testIdPrefix={`day-${day.day_of_week}`}
                        onOpenExPicker={(blockIdx) => setShowExPicker({ blockIdx, target: day.day_of_week })}
                        onJumpPrescribedChange={handleJumpPrescribedChange}
                        onJumpPrescribedSet={handleJumpPrescribedSet}
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Exercise Selector */}
      <ExerciseSelector
        open={!!showExPicker}
        onClose={() => setShowExPicker(null)}
        onSelect={handleExerciseSelected}
      />
    </div>
  );
}
